import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Chip,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { FilterAlt as FilterAltIcon } from "@mui/icons-material";

export interface MultiSelectFilterProps {
  label: string;
  labelId?: string;
  options: string[];
  selectedValues: string[];
  tempValues: string[];
  searchText: string;
  onSearchChange: (value: string) => void;
  onTempChange: (values: string[]) => void;
  onOk: () => void;
  onCancel: () => void;
  onOpen: () => void;
  menuWidth?: number;
  loading?: boolean;
}

const MultiSelectFilter: React.FC<MultiSelectFilterProps> = React.memo(({
  label,
  labelId,
  options,
  selectedValues,
  tempValues,
  searchText,
  onSearchChange,
  onTempChange,
  onOk,
  onCancel,
  onOpen,
  menuWidth = 300,
  loading = false,
}) => {
  const [open, setOpen] = useState(false);

  // Memoize filtered options to prevent recalculation on every render
  const filteredOptions = useMemo(() => 
    options.filter(option =>
      option.toLowerCase().includes(searchText.toLowerCase())
    ),
    [options, searchText]
  );

  // Memoize selection state
  const allFilteredSelected = useMemo(() => 
    filteredOptions.length > 0 && 
    filteredOptions.every(option => tempValues.includes(option)),
    [filteredOptions, tempValues]
  );

  // Memoize MenuProps to prevent recreation
  const menuProps = useMemo(() => ({
    PaperProps: {
      style: {
        maxHeight: 400,
        width: menuWidth,
      },
    },
    autoFocus: false,
    MenuListProps: {
      style: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    anchorOrigin: {
      vertical: 'bottom' as const,
      horizontal: 'left' as const,
    },
    transformOrigin: {
      vertical: 'top' as const,
      horizontal: 'left' as const,
    },
  }), [menuWidth]);

  const handleSelectAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (allFilteredSelected) {
      // Deselect all filtered options
      const newValues = tempValues.filter(v => !filteredOptions.includes(v));
      onTempChange(newValues);
    } else {
      // Select all filtered options
      const newValues = [...new Set([...tempValues, ...filteredOptions])];
      onTempChange(newValues);
    }
  };

  return (
    <FormControl size="small" sx={{ width: 260, minWidth: 260, maxWidth: 260 }}>
      <Select
        labelId={labelId}
        multiple
        value={selectedValues}
        open={open}
        onOpen={() => {
          setOpen(true);
          onOpen();
        }}
        onClose={() => setOpen(false)}
        IconComponent={FilterAltIcon}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#666', textTransform: 'uppercase', fontSize: '11px' }}>
              {label}
            </Typography>
            {selected.length > 0 && selected.length < options.length && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                {selected.slice(0, 5).map((value) => (
                  <Chip key={value} label={value} size="small" sx={{ height: 20, fontSize: '11px' }} />
                ))}
                {selected.length > 5 && (
                  <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 600, ml: 0.5 }}>
                    +{selected.length - 5} more
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}
        MenuProps={menuProps}
        displayEmpty
        sx={{
          bgcolor: 'white',
          borderRadius: 1,
          '& .MuiSelect-select': {
            py: 0.25,
            px: 0.75,
            display: 'flex',
            alignItems: 'center',
            minHeight: '28px',
          },
          '& .MuiOutlinedInput-root': {
            alignItems: 'center',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d0d0d0',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#b0b0b0',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1976d2',
          },
          '& .MuiSelect-icon': {
            color: '#666',
            fontSize: '15px',
            transform: 'none !important',
          },
        }}
      >
        {/* Search Box */}
        <Box sx={{ px: 2, py: 1, position: 'sticky', top: 0, bgcolor: 'white', zIndex: 1, borderBottom: '1px solid #e0e0e0' }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchText}
            onChange={(e) => {
              e.stopPropagation();
              onSearchChange(e.target.value);
            }}
            onKeyDown={(e) => e.stopPropagation()}
            fullWidth
            autoFocus
            sx={{
              '& .MuiInputBase-root': {
                height: 32,
              }
            }}
          />
        </Box>

        {/* Select All Option */}
        <MenuItem
          onClick={handleSelectAll}
          sx={{ 
            borderBottom: '1px solid #e0e0e0',
            bgcolor: '#f5f5f5',
            py: 0.5,
            minHeight: 'auto',
            '&:hover': {
              bgcolor: '#eeeeee',
            }
          }}
        >
          <Checkbox 
            checked={allFilteredSelected}
            indeterminate={tempValues.some(v => filteredOptions.includes(v)) && !allFilteredSelected}
            size="small"
            sx={{ py: 0.25 }}
          />
          <ListItemText 
            primary="(Select All)" 
            primaryTypographyProps={{ 
              fontWeight: 600,
              fontSize: '11px'
            }}
          />
        </MenuItem>

        {/* Loading indicator */}
        {loading && (
          <Box sx={{ px: 2, py: 2, textAlign: 'center', color: '#666' }}>
            <Typography variant="caption">Loading options...</Typography>
          </Box>
        )}

        {/* Options List */}
        {!loading && filteredOptions.map((option) => (
          <MenuItem
            key={option}
            value={option}
            onClick={(e) => {
              e.stopPropagation();
              const newValues = tempValues.includes(option)
                ? tempValues.filter(v => v !== option)
                : [...tempValues, option];
              onTempChange(newValues);
            }}
            sx={{
              py: 0.5,
              minHeight: 'auto',
            }}
          >
            <Checkbox 
              checked={tempValues.includes(option)} 
              size="small"
              sx={{ py: 0.25 }}
            />
            <ListItemText 
              primary={option}
              primaryTypographyProps={{
                fontSize: '11px'
              }}
            />
          </MenuItem>
        ))}

        {/* Action Buttons */}
        <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1, justifyContent: 'flex-end', borderTop: '1px solid #e0e0e0', position: 'sticky', bottom: 0, bgcolor: 'white', zIndex: 1 }}>
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onCancel();
              setOpen(false);
            }}
            sx={{ textTransform: 'none', minWidth: 70 }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={(e) => {
              e.stopPropagation();
              onOk();
              setOpen(false);
            }}
            sx={{ textTransform: 'none', minWidth: 70, bgcolor: '#1976d2' }}
          >
            OK
          </Button>
        </Box>
      </Select>
    </FormControl>
  );
});

MultiSelectFilter.displayName = 'MultiSelectFilter';

export default MultiSelectFilter;
